// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getRebuyData, getRebuySettings } from 'JsComponents/rebuy-data.js';

// Rebuy custom rule id (product source). NOTE: distinct from the widget id (295807),
// which drives discount settings via getRebuySettings(). The widget's settings endpoint
// resolves to this rule (304460).
const RULE_ID = '304460';
const MAX_REBUY_PRODUCTS = 2;

const CheckboxCheckedIcon = () => (
    <svg
        width="17"
        height="17"
        viewBox="0 0 17 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <rect width="17" height="17" rx="3" fill="#E3000B" />
        <path
            d="M3.5 8.5L7 12L13.5 5"
            stroke="white"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const CheckboxUncheckedIcon = () => (
    <svg
        width="17"
        height="17"
        viewBox="0 0 17 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <rect width="17" height="17" rx="3" fill="white" stroke="#dadada" strokeWidth="1" />
    </svg>
);

const processRebuyData = (rebuyData, primaryProduct) => {
    // Unit normalization: Shopify Liquid price is in CENTS (÷100 → dollars);
    // the Rebuy API returns add-on prices already in DOLLARS (no division below).
    const primaryOriginalPrice = primaryProduct.price
        ? parseFloat(primaryProduct.price) / 100
        : 0;

    // Trust the variant's `available` flag — Shopify already accounts for inventory policy,
    // untracked inventory, and continue-selling. Do NOT gate on inventory_quantity: untracked
    // products report 0 yet are fully purchasable.
    const isPrimaryAvailable = primaryProduct.product_available?.available !== false;

    const primary = {
        id: primaryProduct.id,
        variant_id:
            primaryProduct.selected_or_first_available_variant?.id || primaryProduct.id,
        image: primaryProduct.image?.src || primaryProduct.image,
        name: primaryProduct.title,
        price: primaryOriginalPrice,
        type: 'primary',
        isUnavailable: !isPrimaryAvailable,
    };

    const addOns = [];
    if (rebuyData?.data && Array.isArray(rebuyData.data)) {
        let count = 0;
        for (const item of rebuyData.data) {
            if (item.id === primaryProduct.id) continue;
            if (count >= MAX_REBUY_PRODUCTS) break;

            const variant = item.variants?.[0] || {};
            const image =
                item.image?.src || item.image || item.images?.[0]?.src || null;
            const originalPrice = variant.price
                ? parseFloat(variant.price)
                : parseFloat(item.price || 0);

            // Product URL for the add-on card link. Rebuy returns `url` (and/or `handle`);
            // fall back to the canonical /products/<handle> path, or null when neither exists.
            const productUrl = item.url || (item.handle ? `/products/${item.handle}` : null);

            addOns.push({
                id: item.id,
                variant_id: variant.id || item.id,
                image,
                name: item.title,
                price: originalPrice,
                url: productUrl,
                type: 'bundle',
                isUnavailable: false,
            });
            count++;
        }
    }

    return [primary, ...addOns];
};

// Compare-at vs bundle total for the current selection. The discount only applies when the whole
// bundle is selected (allSelected), so a partial selection sums to full price on both totals.
const calcBundlePricing = (primaryProduct, bundleProducts, selectedProducts, discountPercent, allSelected) => {
    const selectedRows = [
        ...(primaryProduct ? [primaryProduct] : []),
        ...bundleProducts.filter((p) => selectedProducts.has(p.id)),
    ];
    const factor = allSelected ? 1 - discountPercent / 100 : 1;

    return selectedRows.reduce(
        (totals, p) => ({
            compareTotal: totals.compareTotal + p.price,
            bundleTotal: totals.bundleTotal + p.price * factor,
        }),
        { compareTotal: 0, bundleTotal: 0 }
    );
};

const formatPrice = (dollars) => `$${dollars.toFixed(2)}`;

// Line-item properties that tag a COMPLETE bundle for Rebuy's automatic per-line discount. The
// unique _rebuy_bundle_id keeps each add its own group (never merges with a standalone line) and lets
// rebuy-bundle-validator detect a broken bundle via _rebuy_bundle_size and revert the survivors.
const buildBundleProperties = (widgetId, bundleSize) => ({
    _source: 'Rebuy',
    _widget_id: widgetId,
    _attribution: 'Rebuy Dynamic Product Bundle',
    _rebuy_bundle_id: `rb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    _rebuy_bundle_size: bundleSize,
});

// POST the bundle to the cart and hand the result to the theme's cart-drawer for a live re-render
// (Dawn pattern, mirrors free-gift-modal — no page refresh). Throws on a cart API error.
const postBundleToCart = async (items, cartDrawer) => {
    const sections =
        cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
            ? cartDrawer.getSectionsToRender().map((section) => section.id).join(',')
            : 'cart-drawer,cart-icon-bubble';

    const formData = new FormData();
    items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        Object.keys(item.properties).forEach((key) => {
            formData.append(`items[${index}][properties][${key}]`, item.properties[key]);
        });
    });
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);

    const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
    });

    const data = await response.json();
    if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Failed to add to cart');
    }

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
        // renderContents only clears `is-empty` from `.drawer__inner`, not the root — so the first
        // add into an empty cart leaves the drawer hidden. Clear it on the root too.
        cartDrawer.classList.remove('is-empty');
    }
};

// Product types whose main Add-to-Cart opens the consent/demographic modal before adding.
const CONSENT_GATE_TYPES = ['demographic_info', 'testing_kit'];

// Mirror the main button's rule: read the page's product-form submit button and return its
// gating product_type (or null). Keeps the Rebuy gate consistent with the main flow without
// threading template data through Liquid.
const getConsentGateType = () => {
    const btn = document.querySelector('.product-form__submit[data-product-type]');
    const type = btn && btn.dataset.productType;
    return CONSENT_GATE_TYPES.includes(type) ? type : null;
};

const FrequentlyBought = ({ shopifyData = {} }) => {
    const productData = shopifyData.data?.[0];

    const widgetId = productData?.widget_id || '';
    const heading = productData?.heading || 'Get the full routine';
    const subheading = productData?.subheading || 'Bundle supplements';
    const pillText = productData?.pill_text || '';
    // UI Discount: a positive percent here overrides the real Rebuy discount for DISPLAY
    // pricing exclusively. It never tags cart lines, so checkout still charges full/real price —
    // purely a visual discount. Blank/0/invalid → fall back to the real Rebuy discount.
    const uiDiscountPercent = (() => {
        const parsed = parseFloat(productData?.ui_discount_percent);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    })();

    const [productList, setProductList] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    const [rebuySettings, setRebuySettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [addingToCart, setAddingToCart] = useState(false);
    // True when every currently-selected bundle variant (primary + checked add-ons) is already
    // in the cart — drives the disabled "Added" CTA. Derived from /cart.js so it survives reloads.
    const [inCart, setInCart] = useState(false);
    // False until the first /cart.js check resolves. The check is async, so without this gate the
    // CTA would render enabled ("Add N to cart") for the fetch's duration on every refresh and then
    // snap to the disabled "Added" state — a visible flash. Keep the CTA disabled until we actually
    // know the cart state.
    const [cartChecked, setCartChecked] = useState(false);
    // The primary variant currently chosen in the on-page variant picker. The picker writes its
    // selection into the product form's hidden id input and fires `change`; we mirror it here so
    // the bundle re-prices/re-checks against the active variant (and the CTA re-enables when the
    // newly-picked variant isn't in the cart yet). '' until read from the DOM.
    const [pickedPrimaryId, setPickedPrimaryId] = useState('');

    useEffect(() => {
        if (!productData?.id) return;

        setLoading(true);
        // Resilient: a failed Rebuy call must NOT blank the widget. Each call degrades to
        // null so Promise.all never rejects — processRebuyData then still yields the primary
        // product row, keeping the section visible even on rate-limited reloads.
        Promise.all([
            getRebuyData({ ruleId: RULE_ID, productId: productData.id, limit: 10 }).catch(
                (error) => {
                    console.error('Rebuy data fetch failed (showing primary only):', error);
                    return null;
                }
            ),
            getRebuySettings(widgetId).catch((error) => {
                console.error('Rebuy settings fetch failed:', error);
                return null;
            }),
        ]).then(([rebuyData, settings]) => {
            setRebuySettings(settings);
            const processed = processRebuyData(rebuyData, productData);
            setProductList(processed);

            const defaultSelected = new Set(
                processed.filter((p) => p.type === 'bundle').map((p) => p.id)
            );
            setSelectedProducts(defaultSelected);
            setLoading(false);
        });
    }, [productData?.id, widgetId]);

    // Derived selection state — single source of truth, reused by pricing, the CTA, and the add flow.
    const primaryProduct = useMemo(
        () => productList.find((p) => p.type === 'primary'),
        [productList]
    );
    const bundleProducts = useMemo(
        () => productList.filter((p) => p.type === 'bundle'),
        [productList]
    );
    // The Rebuy discount only applies to the COMPLETE bundle (primary + every add-on).
    const allAddOnsSelected = useMemo(
        () => bundleProducts.length > 0 && bundleProducts.every((p) => selectedProducts.has(p.id)),
        [bundleProducts, selectedProducts]
    );

    // Active selection as ordered lines. The primary line tracks the variant currently
    // chosen in the on-page picker (pickedPrimaryId), falling back to the load-time variant
    // until the picker is read; add-ons use their own variant. isPrimary lets the add flow
    // stamp consent props on the primary line without a fragile id comparison.
    const selectedLines = useMemo(() => {
        const lines = [];
        for (const p of productList) {
            if (p.type === 'primary') {
                lines.push({ variantId: pickedPrimaryId || p.variant_id, isPrimary: true });
            } else if (selectedProducts.has(p.id)) {
                lines.push({ variantId: p.variant_id, isPrimary: false });
            }
        }
        return lines;
    }, [productList, selectedProducts, pickedPrimaryId]);

    // Variant ids of the active selection — drives the authoritative in-cart check.
    const selectedVariantIds = useMemo(
        () => selectedLines.map((line) => line.variantId),
        [selectedLines]
    );

    // Mirror the on-page variant picker. It writes the chosen id into the product form's
    // hidden input and fires `change` (precision-variant-picker.js → updateFormVariantId).
    // Tracking it here re-prices/re-checks the bundle against the active variant, so the CTA
    // re-enables when a freshly-picked variant isn't in the cart yet. Seeds from the current
    // DOM value on mount (the input exists in Liquid independently of this widget loading).
    useEffect(() => {
        const input = document.querySelector('input[name="id"].product-variant-id');
        if (!input) return undefined;
        const sync = () => setPickedPrimaryId(input.value || '');
        sync();
        input.addEventListener('change', sync);
        return () => input.removeEventListener('change', sync);
    }, []);

    // Authoritative cart check: the selection counts as "added" only when EVERY selected variant
    // is present in the live cart. Re-fetches /cart.js rather than trusting any event payload.
    const checkInCart = useCallback(async () => {
        if (selectedVariantIds.length === 0) {
            // Pre-load only (no primary line yet). Do NOT mark cartChecked here — that would
            // unlock the CTA before the real cart check runs, flashing an enabled "Add to cart"
            // the instant rebuy data resolves. Leave it false so the Loading state holds.
            setInCart(false);
            return;
        }
        try {
            const response = await fetch('/cart.js', {
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) return;
            const cart = await response.json();
            // Coerce to Number on both sides: the picker writes input.value (a string) into
            // pickedPrimaryId, while /cart.js variant ids (and Rebuy add-on ids) are numbers.
            // Set<number>.has("12345") is always false, so without this the CTA never flips to
            // "Added" once a variant is picked.
            const cartVariantIds = new Set(
                (cart.items || []).map((item) => Number(item.variant_id ?? item.id))
            );
            setInCart(selectedVariantIds.every((id) => cartVariantIds.has(Number(id))));
        } catch (error) {
            console.error('FBT cart-state check failed:', error);
        } finally {
            // Runs even on the early !response.ok return — mark the first check done so the CTA
            // unlocks (a fetch failure must not leave the button permanently disabled).
            setCartChecked(true);
        }
    }, [selectedVariantIds]);

    // Re-check on mount, when the selection changes, and on any cart update (e.g. an item removed
    // from the drawer) so the CTA re-enables. The theme broadcasts removals via the pubsub
    // 'cart-update' channel (assets/cart.js) and a native 'cart-data-updated' document event.
    useEffect(() => {
        checkInCart();
        const onCartUpdate = () => checkInCart();
        // Browser Back often restores this page from the bfcache: the WHOLE page is frozen from
        // when we left — React does not remount, no cart event fires, and the server-rendered cart
        // drawer + count bubble keep their stale item count. A removal made elsewhere then leaves
        // the page showing the old products (and the CTA stuck) while checkout is correct.
        // `event.persisted` is the bfcache signal; a fresh load reconciles all cart UI at once.
        const onPageShow = (event) => {
            if (event.persisted) {
                window.location.reload();
                return;
            }
            checkInCart();
        };
        let unsubscribe;
        if (typeof window.subscribe === 'function') {
            unsubscribe = window.subscribe('cart-update', onCartUpdate);
        }
        document.addEventListener('cart-data-updated', onCartUpdate);
        window.addEventListener('pageshow', onPageShow);
        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
            document.removeEventListener('cart-data-updated', onCartUpdate);
            window.removeEventListener('pageshow', onPageShow);
        };
    }, [checkInCart]);

    const handleToggle = (productId) => {
        setSelectedProducts((prev) =>
            prev.has(productId)
                ? new Set([...prev].filter((id) => id !== productId))
                : new Set([...prev, productId])
        );
    };

    const handleAddToCart = async () => {
        if (!primaryProduct || selectedVariantIds.length === 0) return;

        // Consent gate: on pages where the main Add-to-Cart opens a consent/demographic modal
        // (testing_kit / demographic_info), the Rebuy bundle must do the same — the bundle
        // includes the primary product, so it can't be allowed to bypass that gate. Collect the
        // properties via the shared <product-page-modal> and stamp them on the primary line only.
        // Run this BEFORE the loading state so the CTA isn't stuck spinning while the modal is open.
        let consentProps = null;
        const gateType = getConsentGateType();
        if (gateType) {
            const modal = document.querySelector('product-page-modal');
            // Fail safe: on a gated product, never add without consent. If the modal isn't
            // available, abort rather than silently bypassing the gate.
            if (!modal || typeof modal.collectProperties !== 'function') return;
            consentProps = await modal.collectProperties(gateType);
            if (!consentProps) return; // customer dismissed the modal — abort the add
        }

        // Tag the lines only when the whole bundle is selected, so Rebuy's per-line discount applies
        // exactly to a complete bundle. A partial selection is added plain (no _source) → full price,
        // matching what the widget shows. The unique bundle id also keeps these lines from merging
        // with a standalone copy of the same variant, so that copy stays a separate full-price line.
        const bundleProps = allAddOnsSelected
            ? buildBundleProperties(widgetId, selectedVariantIds.length)
            : {};
        // selectedLines already carries the picker-chosen primary variant and the isPrimary
        // flag, so the add posts the active variant and stamps consent props on the primary
        // line only — no fragile id comparison or extra DOM read needed here.
        const items = selectedLines.map((line) => ({
            id: line.variantId,
            quantity: 1,
            properties: (consentProps && line.isPrimary)
                ? { ...bundleProps, ...consentProps }
                : bundleProps,
        }));

        try {
            setAddingToCart(true);
            await postBundleToCart(items, document.querySelector('cart-drawer'));
            // Flip the CTA to "Added" immediately (don't wait on the cart-update event round-trip).
            await checkInCart();
        } catch (error) {
            console.error('Error adding bundle to cart:', error);
        } finally {
            setAddingToCart(false);
        }
    };

    // Show the loading placeholder while BOTH rebuy data and the first cart check resolve.
    // Gating on cartChecked too means the CTA never renders before its in-cart state is known.
    if (loading || !cartChecked) {
        return (
            <div className="frequently-bought__card tw-flex tw-items-center tw-justify-center tw-py-[24px]">
                <span className="tw-text-[14px] tw-text-[#7b7b7b]">Loading...</span>
            </div>
        );
    }

    // Only show the widget when there's at least one add-on to bundle. A primary-only list
    // (no Rebuy products, or only the current product) is a meaningless "bundle of one" — hide it.
    const hasAddOns = productList.some((p) => p.type === 'bundle');
    if (!hasAddOns) return null;

    const discount = rebuySettings?.data?.discount;
    // Rebuy keeps a stale `amount` when a discount is turned off; `type === 'none'` is the
    // authoritative "no discount" signal. Only apply the percent when a discount is active.
    const discountActive = !!discount?.type && discount.type !== 'none';
    const realDiscountPercent = discountActive ? (discount.amount || 0) : 0;
    // Real Rebuy discount wins when active; the UI Discount is only a display-only fallback
    // for when no real Rebuy discount is set up.
    const discountPercent = realDiscountPercent > 0 ? realDiscountPercent : uiDiscountPercent;
    const { compareTotal, bundleTotal } = calcBundlePricing(
        primaryProduct,
        bundleProducts,
        selectedProducts,
        discountPercent,
        allAddOnsSelected
    );

    const totalCount = 1 + selectedProducts.size;
    const isPrimaryUnavailable = primaryProduct?.isUnavailable || false;
    // The primary product is always part of the bundle, so the CTA is enabled even when no
    // add-ons are selected. Block while adding, if the primary itself is unavailable, or once the
    // current selection is already in the cart ("Added" state — re-enables when an item is removed).
    // The first cart check is guaranteed done here (the loading guard above holds the placeholder
    // until cartChecked), so this only weighs the real states.
    const ctaDisabled = addingToCart || isPrimaryUnavailable || inCart;
    const ctaLabel = addingToCart
        ? 'Adding...'
        : inCart
            ? 'Added'
            : `Add ${totalCount} to cart`;

    return (
        /*
         * Single component — responsive via tw- classes.
         * Desktop: bordered outer card (md:tw-border), items-center rows, right-side label/checkbox.
         * Mobile: flat (no outer border), items-start rows, stacked label/checkbox, full-width button.
         */
        <div
            className="frequently-bought__card
                tw-flex tw-flex-col tw-gap-[12px]
                md:tw-gap-[16px] md:tw-border-[0.75px] md:tw-border-solid md:tw-border-[#dfdfdf]
                md:tw-rounded-[15px] md:tw-px-[12px] md:tw-py-[16px]"
        >
            {/* ── Header ── (heading block + discount pill) */}
            <div className="frequently-bought__header tw-flex tw-flex-nowrap md:tw-flex-wrap tw-items-start md:tw-items-center tw-justify-between tw-gap-x-[8px] tw-gap-y-[4px]">
                <div className="tw-flex tw-flex-col tw-min-w-0 md:tw-min-w-[unset] tw-gap-[4px] md:tw-gap-[8px]">
                    <h2 className="frequently-bought__heading tw-m-0 tw-break-words tw-font-[Montserrat] tw-font-bold tw-text-[16px] tw-leading-[24px] tw-text-[#000000]">
                        {heading}
                    </h2>
                    <p className="frequently-bought__subheading tw-m-0 tw-break-words tw-font-[Montserrat] tw-font-normal tw-text-[14px] tw-leading-[20px] tw-text-[#000000]">
                        {subheading}
                    </p>
                </div>

                {/* Pill — from block setting; hidden when blank */}
                {pillText && (
                    <span
                        className="frequently-bought__pill
                            tw-inline-flex tw-items-center tw-shrink-0 tw-px-9 tw-py-5
                            tw-border-[0.75px] tw-border-solid tw-border-[#E3000B] tw-rounded-full
                            tw-font-[Montserrat] tw-font-semibold tw-text-[11px] tw-leading-[16px] tw-tracking-[0.5px] tw-text-[#E3000B]
                            tw-whitespace-nowrap tw-uppercase"
                    >
                        {pillText}
                    </span>
                )}
            </div>

            {/* ── Body: product rows + footer, all 12px apart (matches Figma rows-wrapper) ── */}
            <div className="frequently-bought__body tw-flex tw-flex-col tw-gap-[12px]">
                {productList.map((product) => {
                    const isPrimary = product.type === 'primary';
                    const isChecked = selectedProducts.has(product.id);
                    const checkboxId = `fb-addon-${product.id}`;
                    // Add-on (non-primary) cards link out to their product page; the primary
                    // card is the current product, so it stays non-clickable.
                    const productUrl = !isPrimary ? product.url : null;
                    // Show the strikethrough original + discounted price only when the bundle
                    // qualifies (all add-ons selected); otherwise just the full price.
                    const rowDiscounted = product.price * (1 - discountPercent / 100);
                    const showDiscount = allAddOnsSelected && discountPercent > 0;

                    return (
                        <div
                            key={product.id}
                            className={`frequently-bought__row ${isPrimary ? 'frequently-bought__row--primary' : 'frequently-bought__row--addon'}
                                tw-flex tw-items-start md:tw-items-center
                                tw-gap-[14px]
                                tw-border-[0.75px] tw-border-solid tw-border-[#d4d4d4] md:tw-border-[#dadada]
                                tw-rounded-[8px] tw-px-[12px] tw-py-[8px]`}
                        >
                            {/* Image — skipped when no source to avoid a broken-image icon.
                                Add-on cards link the image to their product page. */}
                            {product.image && (
                                productUrl ? (
                                    <a
                                        href={productUrl}
                                        className="frequently-bought__row-image-link tw-flex-shrink-0"
                                        aria-label={product.name}
                                    >
                                        <img
                                            className="frequently-bought__row-image tw-flex-shrink-0 tw-w-[55px] tw-h-[55px] tw-rounded-[8px] tw-object-cover"
                                            src={product.image}
                                            alt={product.name}
                                            width="55"
                                            height="55"
                                            loading="lazy"
                                        />
                                    </a>
                                ) : (
                                    <img
                                        className="frequently-bought__row-image tw-flex-shrink-0 tw-w-[55px] tw-h-[55px] tw-rounded-[8px] tw-object-cover"
                                        src={product.image}
                                        alt={product.name}
                                        width="55"
                                        height="55"
                                        loading="lazy"
                                    />
                                )
                            )}

                            {/* Content: stacked on mobile, row + space-between on desktop. Single control instance. */}
                            <div className="frequently-bought__row-content tw-flex tw-flex-1 tw-min-w-0 tw-flex-col tw-gap-[4px] md:tw-flex-row md:tw-items-center md:tw-justify-between md:tw-gap-[14px]">
                                <div className="frequently-bought__row-text tw-flex tw-flex-col tw-gap-[4px] tw-min-w-0">
                                    {productUrl ? (
                                        <a
                                            href={productUrl}
                                            className="frequently-bought__row-title tw-font-[Montserrat] tw-font-semibold tw-text-[14px] tw-leading-[20px] tw-text-[#000000] tw-no-underline hover:tw-underline"
                                        >
                                            {product.name}
                                        </a>
                                    ) : (
                                        <span className="frequently-bought__row-title tw-font-[Montserrat] tw-font-semibold tw-text-[14px] tw-leading-[20px] tw-text-[#000000]">
                                            {product.name}
                                        </span>
                                    )}
                                    {/* Price line replaces the subtitle slot: original (strikethrough) then discounted. */}
                                    <span className="frequently-bought__row-price tw-flex tw-items-baseline tw-gap-[6px]">
                                        {showDiscount && (
                                            <span className="frequently-bought__row-compare tw-font-[Montserrat] tw-font-medium tw-text-[12px] tw-leading-[16px] tw-text-[#7b7b7b] tw-line-through">
                                                {formatPrice(product.price)}
                                            </span>
                                        )}
                                        <span className="frequently-bought__row-amount tw-font-[Montserrat] tw-font-medium tw-text-[12px] tw-leading-[16px] tw-text-[#000000]">
                                            {formatPrice(showDiscount ? rowDiscounted : product.price)}
                                        </span>
                                    </span>
                                </div>

                                {/* Control — "This Product" label (primary) or checkbox (add-on).
                                    Primary label sits below price on mobile via order-last, resets on desktop. */}
                                {isPrimary ? (
                                    <span className="frequently-bought__row-label tw-flex-shrink-0 tw-order-last md:tw-order-none tw-font-[Montserrat] tw-font-medium tw-text-[12px] tw-leading-[16px] tw-text-[#818181]">
                                        This Product
                                    </span>
                                ) : (
                                    <label
                                        htmlFor={checkboxId}
                                        className="frequently-bought__row-checkbox tw-flex tw-flex-shrink-0 tw-items-center tw-gap-[8px] tw-cursor-pointer"
                                    >
                                        <input
                                            id={checkboxId}
                                            type="checkbox"
                                            className="tw-sr-only"
                                            checked={isChecked}
                                            onChange={() => handleToggle(product.id)}
                                        />
                                        {isChecked ? <CheckboxCheckedIcon /> : <CheckboxUncheckedIcon />}
                                        <span className="frequently-bought__row-addon-label tw-font-[Montserrat] tw-font-medium tw-text-[12px] tw-leading-[16px] tw-text-[#1d1d1d]">
                                            Add to bundle
                                        </span>
                                    </label>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* ── Footer: price + delivery (left) / CTA (right on desktop, full-width on mobile) ── */}
                <div className="frequently-bought__footer tw-flex tw-flex-col tw-gap-[12px] md:tw-flex-row md:tw-items-center md:tw-justify-between">
                    <div
                        className="frequently-bought__price-area tw-flex tw-flex-wrap tw-items-baseline tw-gap-[8px]"
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        {allAddOnsSelected && compareTotal > bundleTotal && (
                            <span className="frequently-bought__compare-price tw-font-[Montserrat] tw-font-semibold tw-text-[16px] tw-leading-[24px] tw-text-[#8b8b8b] tw-line-through">
                                {formatPrice(compareTotal)}
                            </span>
                        )}
                        <span className="frequently-bought__bundle-price tw-font-[Montserrat] tw-font-semibold tw-text-[16px] tw-leading-[24px] tw-text-[#000000]">
                            {formatPrice(bundleTotal)}
                        </span>
                    </div>

                    <button
                        type="button"
                        className="frequently-bought__cta
                            tw-flex tw-items-center tw-justify-center tw-shrink-0
                            tw-font-[Montserrat] tw-font-medium tw-text-[16px] tw-leading-[24px] tw-text-[#ffffff]
                            tw-border-0 tw-rounded-[8px]
                            tw-h-[37px] tw-w-full md:tw-w-[149px]
                            tw-cursor-pointer tw-bg-[#b82b35]
                            disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        onClick={handleAddToCart}
                        disabled={ctaDisabled}
                    >
                        {ctaLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FrequentlyBought;
